import { supabase } from '../lib/supabase';
import { companyService } from './companyService';
import { siteService } from './siteService';


export const setupService = {
  // Check if setup is complete
  async isSetupComplete() {
    try {
      const { data, error } = await companyService?.getUserCompany();
      if (error) throw error;
      return { data: data?.setup_complete || false, error: null };
    } catch (error) {
      return { data: false, error: { message: error?.message } };
    }
  },

  // Update company details (Step 1)
  async updateCompanyDetails(companyId, companyData) {
    try {
      const { data, error } = await supabase?.from('companies')?.update({
          company_name: companyData?.companyName,
          country: companyData?.country
        })?.eq('id', companyId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Create first site (Step 2) - with optional address
  async createFirstSite(companyId, siteName = 'Main Site', siteAddress = '') {
    try {
      // Idempotent guard: back-navigation, double-submit, or two open tabs must
      // not create duplicate sites. If a non-archived site with this name already
      // exists for the company, return that row instead of inserting again. The
      // partial unique index (R4U3iRGh PR 1) is the DB backstop; this returns the
      // existing row cleanly rather than letting a 23505 bubble up. Match on
      // lower(btrim(...)) in JS to mirror the index's normalisation exactly.
      const target = siteName?.trim()?.toLowerCase();
      const { data: existingSites } = await supabase
        ?.from('sites')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.neq('status', 'archived');
      const existing = existingSites?.find(s => s?.site_name?.trim()?.toLowerCase() === target);
      if (existing) {
        return { data: existing, error: null };
      }

      const siteData = {
        companyId,
        siteName,
        siteReference: 'SITE-001',
        addressLine1: siteAddress || 'To be updated',
        city: 'To be updated',
        postcode: siteAddress || 'TBD',
        clientName: 'To be updated',
        clientContactPerson: 'To be updated',
        clientEmail: 'tbd@example.com',
        clientPhone: '07000000000',
        status: 'active',
        startDate: new Date()?.toISOString()?.split('T')?.[0],
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0]
      };

      return await siteService?.createSite(siteData);
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Create first site during onboarding - bypasses plan limit check
  // Used when the company already has active sites (e.g. demo environment)
  async createFirstSiteForOnboarding(companyId, siteName = 'Main Site', siteAddress = '') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Idempotent guard (same as createFirstSite): return an existing
      // non-archived site of the same name instead of inserting a duplicate on
      // replay. The documented plan-limit bypass below stays intentional (the
      // demo environment may already have active sites) — this existence check
      // just sits on top of it.
      const target = siteName?.trim()?.toLowerCase();
      const { data: existingSites } = await supabase
        ?.from('sites')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.neq('status', 'archived');
      const existing = existingSites?.find(s => s?.site_name?.trim()?.toLowerCase() === target);
      if (existing) {
        return { data: existing, error: null };
      }

      const { data, error } = await supabase?.from('sites')?.insert({
        company_id: companyId,
        // Trim at the write boundary — stored site_name is compared to
        // already-trimmed CSV cells later (DNllfNne).
        site_name: siteName?.trim(),
        site_reference: 'SITE-001',
        address_line1: siteAddress?.trim() || 'To be updated',
        city: 'To be updated',
        postcode: siteAddress?.trim() || 'TBD',
        client_name: 'To be updated',
        client_contact_person: 'To be updated',
        client_email: 'tbd@example.com',
        client_phone: '07000000000',
        status: 'active',
        start_date: new Date()?.toISOString()?.split('T')?.[0],
        end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        created_by: user?.id
      })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Get the most recent active site for this company (fallback during onboarding)
  async getExistingSiteForOnboarding(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('sites')
        ?.select('id, site_name, status')
        ?.eq('company_id', companyId)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      return { data: data || null, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Invite team members (Step 4) - creates user_profiles and assigns to site
  async inviteTeamMembers(companyId, siteId, teamMembers) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // ── Session guard: ensure a valid session before calling edge functions ──
      let { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        // Attempt to refresh the session
        const { data: refreshed } = await supabase?.auth?.refreshSession();
        session = refreshed?.session;
      }
      if (!session?.access_token) {
        throw new Error('Please log in again');
      }
      const accessToken = session?.access_token;

      // Fetch company name for the invite email
      let companyName = 'your company';
      try {
        const { data: companyData } = await supabase
          ?.from('companies')
          ?.select('company_name')
          ?.eq('id', companyId)
          ?.single();
        if (companyData?.company_name) companyName = companyData?.company_name;
      } catch (_) { /* non-fatal */ }

      const results = [];

      for (const member of teamMembers) {
        if (!member?.email?.trim()) continue;

        const memberRole = member?.role || 'worker';

        // Determine effective siteId based on role:
        // - admin/worker: never pass siteId (company-level only)
        // - site_manager: pass siteId only if provided
        // - client: always pass siteId (required)
        const effectiveSiteId = (memberRole === 'admin' || memberRole === 'worker')
          ? null
          : (siteId || null);

        // Check if user already belongs to this specific company
        const { data: existingProfile } = await supabase
          ?.from('user_profiles')
          ?.select('id, role, company_id')
          ?.eq('email', member?.email?.trim())
          ?.maybeSingle();

        if (existingProfile?.id && existingProfile?.company_id === companyId) {
          // Already in company — check site assignment only if siteId provided
          if (effectiveSiteId) {
            const { data: siteAssignment } = await supabase
              ?.from('worker_site_assignments')
              ?.select('id')
              ?.eq('site_id', effectiveSiteId)
              ?.eq('user_id', existingProfile?.id)
              ?.maybeSingle();

            if (siteAssignment?.id) {
              results?.push({ email: member?.email, success: true, type: 'already_on_site', message: 'User already on this site' });
              continue;
            }
          } else {
            results?.push({ email: member?.email, success: true, type: 'already_on_site', message: 'User already in this company' });
            continue;
          }
        }

        // New user OR existing user not yet in this company — let the edge function handle both cases
        // client → invite-client (includes siteName in email)
        // all other roles → invite-worker (handles admin, site_manager, worker)
        const edgeFunction = memberRole === 'client' ? 'invite-client' : 'invite-worker';

        // Fetch site name for client invites
        let siteName = '';
        if (memberRole === 'client' && effectiveSiteId) {
          try {
            const { data: siteData } = await supabase
              ?.from('sites')
              ?.select('site_name')
              ?.eq('id', effectiveSiteId)
              ?.single();
            if (siteData?.site_name) siteName = siteData?.site_name;
          } catch (_) { /* non-fatal */ }
        }

        const invitePayload = memberRole === 'client'
          ? {
              email: member?.email?.trim(),
              fullName: member?.name?.trim() || member?.email?.trim(),
              companyId,
              companyName,
              siteId: effectiveSiteId,
              siteName: siteName || 'Your site',
              invitedBy: user?.id
            }
          : {
              email: member?.email?.trim(),
              fullName: member?.name?.trim() || member?.email?.trim(),
              role: memberRole,
              companyId,
              companyName,
              siteId: effectiveSiteId,
              invitedBy: user?.id
            };

        const { data: inviteResult, error: inviteError } = await supabase?.functions?.invoke(
          edgeFunction,
          {
            body: invitePayload,
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        // Extract the real error message from the JSON body returned by the edge function.
        // inviteError.message from supabase-js is often the generic
        // "Edge Function returned a non-2xx status code" — the real cause is in inviteResult?.error.
        const rawError = inviteResult?.error;
        const realErrMsg = typeof rawError === 'string'
          ? rawError
          : (rawError?.message || rawError?.msg || rawError?.hint || JSON.stringify(rawError) || null);

        // Also capture errorDetail and errorCode for richer diagnostics
        const errorDetail = inviteResult?.errorDetail;
        const errorCode = inviteResult?.errorCode;
        const fullErrMsg = realErrMsg
          ? (errorCode ? `[${errorCode}] ${realErrMsg}` : realErrMsg)
          : null;

        // Capture HTTP status from the FunctionsHttpError so we can distinguish
        // 429 (rate-limited resend) from 5xx (transient) from 4xx (other).
        const inviteHttpStatus = inviteError?.context?.status;

        if (inviteError && !inviteResult) {
          // Network-level / non-JSON failure
          let netMsg = 'Invite failed. Please try again.';
          if (inviteHttpStatus === 429) netMsg = 'Service is busy, please retry in a moment.';
          else if (typeof inviteHttpStatus === 'number' && inviteHttpStatus >= 500) netMsg = 'Service temporarily unavailable, please retry shortly.';
          results?.push({ email: member?.email, success: false, type: 'error', error: netMsg });
          continue;
        }

        if (fullErrMsg) {
          let bodyMsg = 'Invite could not be sent. Please try again.';
          if (inviteHttpStatus === 429) bodyMsg = 'Service is busy, please retry in a moment.';
          else if (typeof inviteHttpStatus === 'number' && inviteHttpStatus >= 500) bodyMsg = 'Service temporarily unavailable, please retry shortly.';
          results?.push({ email: member?.email, success: false, type: 'error', error: bodyMsg });
          continue;
        }

        // Edge function returned success: true but emailSent: false means DB record
        // was created but the email send failed — treat as a real failure.
        if (inviteResult?.success === false || inviteResult?.emailSent === false) {
          results?.push({ email: member?.email, success: false, type: 'error', error: 'User was added, but the invite email could not be sent. Please resend the invite.' });
          continue;
        }

        // Client-side fallback: pre-create site assignment for worker/site_manager if siteId provided
        const invitedUserId = inviteResult?.userId;
        if (invitedUserId && effectiveSiteId && (memberRole === 'worker' || memberRole === 'site_manager')) {
          await supabase
            ?.from('worker_site_assignments')
            ?.upsert({
              user_id: invitedUserId,
              site_id: effectiveSiteId,
              company_id: companyId,
              assignment_date: new Date()?.toISOString()?.split('T')?.[0],
            }, { onConflict: 'user_id,site_id' });
        }

        const isExistingUser = existingProfile?.id != null;
        const resultMessage = inviteResult?.message || (isExistingUser ? 'User added — login link sent' : 'Invite sent');
        results?.push({ email: member?.email, success: true, type: isExistingUser ? 'info' : 'success', message: resultMessage });

        if (isExistingUser && existingProfile?.id) {
          const { notificationService: ns } = await import('./notificationService');
          ns?.notifyUserJoined(
            existingProfile?.id,
            existingProfile?.full_name || member?.email,
            companyId
          )?.catch(() => {});
        }
      }

      return { data: results, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Invite team members with per-member site assignment (used by invite-users page)
  // Each member carries its own siteId. Site is only required for clients.
  async inviteTeamMembersPerRole(companyId, teamMembers) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // ── Session guard: ensure a valid session before calling edge functions ──
      let { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.access_token) {
        // Attempt to refresh the session
        const { data: refreshed } = await supabase?.auth?.refreshSession();
        session = refreshed?.session;
      }
      if (!session?.access_token) {
        throw new Error('Please log in again');
      }
      const accessToken = session?.access_token;

      let companyName = 'your company';
      try {
        const { data: companyData } = await supabase
          ?.from('companies')
          ?.select('company_name')
          ?.eq('id', companyId)
          ?.single();
        if (companyData?.company_name) companyName = companyData?.company_name;
      } catch (_) { /* non-fatal */ }

      const results = [];

      for (const member of teamMembers) {
        if (!member?.email?.trim()) continue;

        const memberRole = member?.role || 'worker';

        // Determine effective siteId per role rules:
        // - admin/worker: never assign a site at invite time
        // - site_manager: use provided siteId if any (optional)
        // - client: use provided siteId (required — validated in UI)
        const effectiveSiteId = (memberRole === 'admin' || memberRole === 'worker')
          ? null
          : (member?.siteId || null);

        // Check if already in company
        const { data: existingProfile } = await supabase
          ?.from('user_profiles')
          ?.select('id, role, company_id, is_active')
          ?.eq('email', member?.email?.trim())
          ?.maybeSingle();

        // Only skip if user is fully active — pending users (is_active=false) need resend
        if (existingProfile?.id && existingProfile?.company_id === companyId && existingProfile?.is_active === true) {
          results?.push({ email: member?.email, success: true, type: 'already_on_site', message: 'User already in this company' });
          continue;
        }

        const edgeFunction = memberRole === 'client' ? 'invite-client' : 'invite-worker';

        let siteName = '';
        if (memberRole === 'client' && effectiveSiteId) {
          try {
            const { data: siteData } = await supabase
              ?.from('sites')
              ?.select('site_name')
              ?.eq('id', effectiveSiteId)
              ?.single();
            if (siteData?.site_name) siteName = siteData?.site_name;
          } catch (_) { /* non-fatal */ }
        }

        const invitePayload = memberRole === 'client'
          ? {
              email: member?.email?.trim(),
              fullName: member?.name?.trim() || member?.email?.trim(),
              companyId,
              companyName,
              siteId: effectiveSiteId,
              siteName: siteName || 'Your site',
              invitedBy: user?.id
            }
          : {
              email: member?.email?.trim(),
              fullName: member?.name?.trim() || member?.email?.trim(),
              role: memberRole,
              companyId,
              companyName,
              siteId: effectiveSiteId,
              invitedBy: user?.id
            };

        // ── DEBUG: log exact payload before sending to edge function ──────────
        console.log(
          `[setupService][inviteTeamMembersPerRole] PAYLOAD → ${edgeFunction}`,
          JSON.stringify(invitePayload, null, 2)
        );

        const { data: inviteResult, error: inviteError } = await supabase?.functions?.invoke(
          edgeFunction,
          {
            body: invitePayload,
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        // ── Extract the real server-side error from the edge function response ──
        // supabase-js throws a generic "Edge Function returned a non-2xx status code"
        // and sets inviteResult to null. We need to read the actual JSON body.
        // The raw response body is attached to the FunctionsHttpError as `context`.
        let serverErrorBody = null;
        if (inviteError && !inviteResult) {
          try {
            // FunctionsHttpError exposes the raw Response on .context
            const ctx = inviteError?.context;
            if (ctx && typeof ctx?.json === 'function') {
              serverErrorBody = await ctx?.json()?.catch(() => null);
            } else if (ctx && typeof ctx?.text === 'function') {
              const txt = await ctx?.text()?.catch(() => '');
              try { serverErrorBody = JSON.parse(txt); } catch (_e) { serverErrorBody = { _raw: txt }; }
            }
          } catch (_) { /* ignore parse errors */ }
        }

        const rawError = serverErrorBody ?? inviteResult?.error;
        const realErrMsg = typeof rawError === 'string'
          ? rawError
          : (rawError?.message || rawError?.msg || rawError?.hint || JSON.stringify(rawError) || null);
        const errorCode = serverErrorBody?.errorCode ?? inviteResult?.errorCode;
        const failingStep = serverErrorBody?.failingStep ?? inviteResult?.failingStep;
        const errorDetail = serverErrorBody?.errorDetail ?? inviteResult?.errorDetail;
        const postgresError = serverErrorBody?.postgresError ?? inviteResult?.postgresError;
        const resendError = serverErrorBody?.resendError ?? inviteResult?.resendError;
        const authError = serverErrorBody?.authError ?? inviteResult?.authError;

        const fullErrMsg = realErrMsg
          ? (errorCode ? `[${errorCode}] ${realErrMsg}` : realErrMsg)
          : null;

        if (inviteError && !inviteResult) {
          const errPayload = {
            email: member?.email,
            success: false,
            type: 'error',
            error: 'Invite failed. Please try again.',
          };
          if (failingStep) errPayload.failingStep = failingStep;
          if (errorDetail) errPayload.errorDetail = errorDetail;
          if (errorCode) errPayload.errorCode = errorCode;
          if (postgresError) errPayload.postgresError = postgresError;
          if (resendError) errPayload.resendError = resendError;
          if (authError) errPayload.authError = authError;
          if (serverErrorBody) errPayload.serverErrorBody = serverErrorBody;
          results?.push(errPayload);
          continue;
        }
        if (fullErrMsg) {
          results?.push({ email: member?.email, success: false, type: 'error', error: 'Invite could not be sent. Please try again.' });
          continue;
        }
        if (inviteResult?.success === false) {
          results?.push({ email: member?.email, success: false, type: 'error', error: 'Invite failed. Please try again.' });
          continue;
        }

        // Client-side fallback site assignment
        const invitedUserId = inviteResult?.userId;
        if (invitedUserId && effectiveSiteId && (memberRole === 'worker' || memberRole === 'site_manager')) {
          await supabase
            ?.from('worker_site_assignments')
            ?.upsert({
              user_id: invitedUserId,
              site_id: effectiveSiteId,
              company_id: companyId,
              assignment_date: new Date()?.toISOString()?.split('T')?.[0],
            }, { onConflict: 'user_id,site_id' });
        }

        const isExistingUser = existingProfile?.id != null;
        const resultMessage = inviteResult?.message || (isExistingUser ? 'User added — login link sent' : 'Invite sent');
        results?.push({ email: member?.email, success: true, type: isExistingUser ? 'info' : 'success', message: resultMessage });

        if (isExistingUser && existingProfile?.id) {
          const { notificationService: ns } = await import('./notificationService');
          ns?.notifyUserJoined(
            existingProfile?.id,
            existingProfile?.full_name || member?.email,
            companyId
          )?.catch(() => {});
        }
      }

      return { data: results, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Create default checklist (Step 5)
  async createDefaultChecklist(siteId, checklistName = 'Daily Site Checks') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create checklist
      const { data: checklist, error: checklistError } = await supabase?.from('site_checklists')?.insert({
          site_id: siteId,
          checklist_name: checklistName,
          description: 'Daily site checks checklist',
          is_template: false,
          created_by: user?.id
        })?.select()?.single();

      if (checklistError) throw checklistError;

      // Create default checklist items
      const defaultItems = [
        { item_text: 'Site access and security check', item_order: 1, is_required: true },
        { item_text: 'PPE compliance verification', item_order: 2, is_required: true },
        { item_text: 'Equipment inspection', item_order: 3, is_required: true },
        { item_text: 'Hazard identification', item_order: 4, is_required: true },
        { item_text: 'Welfare facilities check', item_order: 5, is_required: true }
      ];

      const itemsToInsert = defaultItems?.map(item => ({
        ...item,
        checklist_id: checklist?.id
      }));

      const { error: itemsError } = await supabase?.from('checklist_items')?.insert(itemsToInsert);

      if (itemsError) throw itemsError;

      return { data: checklist, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Find or update existing default checklist (for setup flow)
  async findOrUpdateDefaultChecklist(siteId, checklistName = 'Daily Site Checks') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if a default checklist already exists for this site.
      // NSherjFh PR 8: a soft-deleted default checklist counts as
      // 'doesn't exist' for the setup flow — we'll create a new one.
      const { data: existingChecklists, error: fetchError } = await supabase
        ?.from('site_checklists')
        ?.select('*')
        ?.eq('site_id', siteId)
        ?.eq('is_template', false)
        ?.is('deleted_at', null)
        ?.order('created_at', { ascending: true })
        ?.limit(1);

      if (fetchError) throw fetchError;

      // If checklist exists, update the name only
      if (existingChecklists && existingChecklists?.length > 0) {
        const existingChecklist = existingChecklists?.[0];
        
        if (existingChecklist?.checklist_name !== checklistName) {
          const { data: updatedChecklist, error: updateError } = await supabase
            ?.from('site_checklists')
            ?.update({ checklist_name: checklistName })
            ?.eq('id', existingChecklist?.id)
            ?.select()
            ?.single();

          if (updateError) throw updateError;
          return { data: updatedChecklist, error: null, action: 'updated' };
        }

        return { data: existingChecklist, error: null, action: 'found' };
      }

      // If no checklist exists, create one
      const { data: newChecklist, error: createError } = await this.createDefaultChecklist(siteId, checklistName);
      if (createError) throw createError;

      return { data: newChecklist, error: null, action: 'created' };
    } catch (error) {
      return { data: null, error: { message: error?.message }, action: 'error' };
    }
  },

  // Mark setup as complete
  async completeSetup(companyId) {
    try {
      const { data, error } = await supabase?.from('companies')?.update({ setup_complete: true })?.eq('id', companyId)?.select()?.single();

      if (error) throw error;

      // Clear localStorage setup progress
      localStorage.removeItem('setupProgress');

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Save setup progress to localStorage
  saveProgress(step, formData) {
    const progress = {
      currentStep: step,
      formData,
      timestamp: new Date()?.toISOString()
    };
    localStorage.setItem('setupProgress', JSON.stringify(progress));
  },

  // Load setup progress from localStorage
  loadProgress() {
    try {
      const saved = localStorage.getItem('setupProgress');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  },

  // Clear setup progress
  clearProgress() {
    localStorage.removeItem('setupProgress');
  }
};